import { useState } from "react";
import { toast } from "react-hot-toast";
import { Copy, Eye, EyeOff, RefreshCw, Code2, Webhook, Check } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { mockApiKey, webhookEventOptions, initialWebhookConfig, type WebhookEventId } from "../mockSettings";

function maskKey(key: string) {
  return `${key.slice(0, 8)}${"•".repeat(12)}${key.slice(-4)}`;
}

export function DeveloperSection() {
  const [apiKey, setApiKey] = useState(mockApiKey);
  const [revealed, setRevealed] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [subscribedEvents, setSubscribedEvents] = useState<WebhookEventId[]>([
    "message.received",
    "order.created",
  ]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };

  const handleRegenerate = () => {
    if (!confirmRegenerate) {
      setConfirmRegenerate(true);
      return;
    }
    const newKey = `sk_live_${Math.random().toString(36).slice(2, 26)}`;
    setApiKey(newKey);
    setRevealed(true);
    setConfirmRegenerate(false);
    toast.success("API key regenerated");
  };

  const toggleEvent = (id: WebhookEventId) => {
    setSubscribedEvents((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
    );
  };

  const handleSaveWebhook = () => {
    toast.success("Webhook settings saved");
  };

  return (
    <div className="space-y-5">
      {/* API Key Card */}
      <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
        <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Code2 className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              API Access &amp; Secret Keys
            </span>
          </div>
          <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Server authorization</span>
        </div>

        <div className="p-3.5 sm:p-4 bg-white/70 dark:bg-slate-900/70 space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <code className="flex-1 truncate rounded-xl border border-slate-200/80 dark:border-slate-800 bg-slate-900 dark:bg-slate-950 px-3 py-2 text-xs font-mono text-slate-100 shadow-inner">
              {revealed ? apiKey : maskKey(apiKey)}
            </code>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-9 px-4 text-xs font-semibold rounded-xl shadow-xs border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                onClick={() => setRevealed((prev) => !prev)}
                aria-label={revealed ? "Hide API key" : "Reveal API key"}
              >
                {revealed ? <EyeOff className="h-3.5 w-3.5 mr-1.5" /> : <Eye className="h-3.5 w-3.5 mr-1.5" />}
                {revealed ? "Hide" : "Reveal"}
              </Button>
              <Button type="button" variant="outline" className="h-9 px-4 text-xs font-semibold rounded-xl shadow-xs border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800" onClick={handleCopy}>
                <Copy className="h-3.5 w-3.5 mr-1.5" />
                Copy
              </Button>
            </div>
          </div>

          <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex justify-end">
            <Button
              type="button"
              variant={confirmRegenerate ? "destructive" : "outline"}
              className="h-9 px-4 text-xs font-semibold rounded-xl shadow-xs border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
              onClick={handleRegenerate}
              onBlur={() => setConfirmRegenerate(false)}
            >
              <RefreshCw className="h-3 w-3 mr-1.5" />
              {confirmRegenerate ? "Confirm regenerate?" : "Regenerate key"}
            </Button>
          </div>
        </div>
      </Card>

      {/* Webhooks Card */}
      <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
        <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-sky-500/10 text-sky-600 dark:text-sky-400">
              <Webhook className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              Webhooks &amp; Real-Time Events
            </span>
          </div>
          <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">HTTP push notifications</span>
        </div>

        <div className="p-3.5 sm:p-4 bg-white/70 dark:bg-slate-900/70 space-y-3">
          <div>
            <Label htmlFor="webhook-url" className="text-[10px] font-semibold text-slate-600 dark:text-slate-400">Endpoint URL</Label>
            <Input
              id="webhook-url"
              type="url"
              placeholder="https://yourdomain.com/webhooks/omnibizchat"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              className="mt-1"
            />
          </div>

          <div className="space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Subscribed Events</span>
            <div className="grid gap-2 sm:grid-cols-2">
              {webhookEventOptions.map((event) => (
                <label
                  key={event.id}
                  htmlFor={`webhook-event-${event.id}`}
                  className="flex items-center gap-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-2.5 shadow-2xs text-xs font-semibold text-slate-800 dark:text-slate-200 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <Checkbox
                    id={`webhook-event-${event.id}`}
                    checked={subscribedEvents.includes(event.id)}
                    onChange={() => toggleEvent(event.id)}
                  />
                  <code className="text-xs font-mono text-slate-700 dark:text-slate-300">{event.label}</code>
                </label>
              ))}
            </div>
          </div>

          <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex justify-end">
            <Button className="h-9 px-5 text-sm font-semibold rounded-xl shadow-xs gap-1.5" onClick={handleSaveWebhook}>
              <Check className="h-4 w-4" />
              Save webhook settings
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
